import { DateTime } from 'luxon';
import Chart, { ChartConfiguration, ChartDataSets } from 'chart.js';
import { COLORS, SELECTED_FUND_COLORS } from '../../../../config/constants';
import { BetterChartComponent } from '../../../general/ChartComponent';
import { useWindowBreakpoints } from '../../../../hooks/useWindowBreakpoints';
import { useTranslation } from 'react-i18next';

Chart.defaults.global.defaultFontFamily = 'museo-sans, sans-serif';
Chart.defaults.global.defaultFontColor = COLORS.bndGrey;
Chart.defaults.global.defaultFontSize = 16;

// Source line style: https://codepen.io/pravileaf/pen/OqyXRK
// Source extension: https://stackoverflow.com/questions/54990176/chart-js-vertical-line-when-hovering-and-shadow-on-line
let draw = Chart.controllers.line.prototype.draw;
Chart.controllers.line = Chart.controllers.line.extend({
    draw: function () {
        if (this.chart.tooltip._active && this.chart.tooltip._active.length) {
            var activePoint = this.chart.tooltip._active[0],
                ctx = this.chart.ctx as CanvasRenderingContext2D,
                x = activePoint.tooltipPosition().x,
                topY = this.chart.scales['y-axis-0'].top,
                bottomY = this.chart.scales['y-axis-0'].bottom;

            // draw line
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x, topY);
            ctx.lineTo(x, bottomY);
            ctx.setLineDash([10, 10]);
            ctx.strokeStyle = '#bbb';
            ctx.stroke();
            ctx.restore();
        }
        draw.apply(this, arguments);
    },
});

type ChartData = {
    id: string;
    label: string;
    data: TimeValueDataPoint[];
};

/**
 * @link t = timestamp in milliseconds
 * @link y = a datavalue
 */
export type TimeValueDataPoint = { t: number; y: number };

const getChartOptions = (isMobile: boolean): ChartConfiguration['options'] => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        // aspectRatio: 2,
        legend: {
            display: false,
        },
        elements: {
            line: {
                tension: 0,
                fill: false,
            },
            point: {
                radius: 0,
                hoverRadius: 4,
            },
        },
        tooltips: {
            mode: 'index',
            intersect: false,
            axis: 'x',
            callbacks: {
                title: function (tooltip, data) {
                    const datapoint = data?.datasets?.[0]?.data?.[tooltip[0]?.index || 0] as { x?: number; t?: number };

                    if (datapoint.t) {
                        return DateTime.fromMillis(datapoint.t).setLocale('nl').toLocaleString(DateTime.DATE_FULL);
                    }

                    return 'Indexwaarde';
                },
                label: function (tooltip, data) {
                    if (!tooltip.value) return `${tooltip.label}: onbekend`;

                    const _value = +tooltip.value;
                    const label = data.datasets?.[tooltip.datasetIndex!].label;

                    if (isMobile) {
                        return `Index: ${_value.toLocaleString('nl-NL')}`;
                    }

                    return `${label}: ${_value.toLocaleString('nl-NL')}`;
                },
            } as Chart.ChartTooltipCallback,
        },
        hover: {
            // disable animations due to https://github.com/chartjs/Chart.js/issues/7266
            animationDuration: 0,
            mode: 'index',
            intersect: false,
            axis: 'x',
        },
        scales: {
            yAxes: [
                {
                    position: 'right',
                    gridLines: {
                        drawBorder: false,
                        drawTicks: true,
                        lineWidth: 1.2,
                        zeroLineWidth: 1.2,
                        color: 'rgba(36, 36, 36, 0.2)',
                        zeroLineColor: 'rgba(36, 36, 36, 0.2)',
                    },
                    ticks: {
                        padding: 8,
                    },
                },
            ],
            xAxes: [
                {
                    distribution: 'linear',

                    type: 'time',
                    bounds: 'data',

                    gridLines: {
                        display: false,
                        drawBorder: true,
                    },
                    ticks: {
                        source: 'labels',
                        minRotation: 0,
                        maxRotation: 30,
                    },
                    time: {
                        displayFormats: {
                            second: 'HH:mm:ss',
                            day: 'D MMM',
                            week: 'D MMM YYYY',
                            year: 'MMM YYYY',
                        },
                    },
                },
            ],
        },
    };
};

export const ValueGrowthChart: React.FC<{
    isLoading: boolean;
    data: ChartData[];
    labels: number[];
}> = ({ data, labels, isLoading }) => {
    const { isMobile } = useWindowBreakpoints();
    const { t } = useTranslation('funds');

    const datasets = data
        .map((d) => ({
            ...d,
            data: d.data.map((point) => {
                // To make sure that the values are rounded with max 2 decimals
                return {
                    y: Math.round(point.y * 100) / 100,
                    t: point.t,
                };
            }),
        }))
        .map(toDataSet);

    const hasData = data.length > 0 && data[0].data.length > 0;

    return (
        <div className="relative w-full" style={{ minHeight: 400 }}>
            {hasData && (
                <BetterChartComponent
                    type="line"
                    data={{ datasets, labels }}
                    options={getChartOptions(isMobile)}
                    height={400}
                />
            )}
            {!hasData && !isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-gray-300 px-4 text-center">{t('funds-comparison-screen-no-data')}</div>
                </div>
            )}
        </div>
    );
};

function toDataSet(chartData: ChartData, index: number): ChartDataSets {
    // cycle fund colors
    let color = SELECTED_FUND_COLORS[index % 3];
    return {
        label: chartData.label,
        data: chartData.data,
        backgroundColor: color,
        borderColor: color,
        pointHoverBackgroundColor: color,
    };
}
